import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import Stats from "../components/Stats";
import { UTILS } from "utils";
import { API } from "api";
import { errorHandler } from "api/api/errorHandler";
import { downloadReports } from "utils/Utils";
import OnlineOderTable from "../components/OnlineOrderTable";
import { errorNotification } from "utils/Utils";
import AppHeading1 from "components/ui/AppHeading1.jsx";
import AppContainer from "components/ui/AppContainer.jsx";
import AppButton from "components/ui/AppButton.jsx";
import AppDatePicker from "components/ui/AppDatePicker.jsx";
import AppSearchInput from "components/ui/AppSearchInput";
import moment from "moment";
const Orders = () => {
  const { t } = useTranslation();

  const [state, setState] = useState({
    loading: false,
    orderHistory: {
      data: [],
      totalPages: 0,
    },
    codEarning: 0,
    onlineEarning: 0,
  });
  const [page, setPage] = useState(1);
  /** ISO YYYY-MM-DD from DatePicker, or null when cleared */
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const setLocalStateData = (key, val) =>
    setState((prevState) => ({ ...prevState, [key]: val }));

  const resetstate = () => {
    setLocalStateData("orderHistory", {
      data: [],
      totalPages: 0,
    });

    setLocalStateData("codEarning", 0);
    setLocalStateData("onlineEarning", 0);
  };

  const toApiDate = (iso) => {
    if (!iso) return "";
    const m = moment(iso, "YYYY-MM-DD", true);
    return m.isValid() ? m.format("DD/MM/YYYY") : "";
  };

  const getOrdersHistory = async (pageNumber) => {
    try {
      const formattedStartDate = toApiDate(startDate);
      const formattedEndDate = toApiDate(endDate);
      
      if (!UTILS.validateSelectedDates(formattedStartDate, formattedEndDate)) return;
      setLocalStateData("loading", true);
      const userDetail = UTILS.getCurrentUser();
      const result = await API.GET_ORDER_HISTORY({
        startDate: formattedStartDate,
        endDate: formattedEndDate,
        brandId: userDetail.brandId,
        page: pageNumber ?? page,
      });
      if (result?.success === 1 && result?.data?.orders) {
        if (result?.data?.orders.length >= 1 && result?.data?.count) {
          setLocalStateData("orderHistory", {
            data: result.data.orders,
            totalPages: result.data.count?.totalOrders,
          });
          const cardEarning = result.data.count.cardMachineOrdersEarning || 0;
          const codeEarning = result.data.count.cashOrdersEarning || 0;
          const totalCodEarning = Number(codeEarning + cardEarning).toFixed(2);
          setLocalStateData("codEarning", totalCodEarning);
          setLocalStateData(
            "onlineEarning",
            result.data.count.onlineOrdersEarning
          );
        } else {
          UTILS.handleSuccesNotification(t("No Order History Found"));
          resetstate();
        }
      } else {
        // reset
        resetstate();
        UTILS.handleErrorNotification(
          t(result?.error?.message || errorHandler(result?.error))
        );
      }
    } catch (error) {
      resetstate();
      UTILS.handleErrorNotification(
        t(error?.error?.message || errorHandler(error?.error))
      );
    } finally {
      setLocalStateData("loading", false);
    }
  };

  const downloadexcel = async () => {
    try {
      const formattedStartDate = toApiDate(startDate);
      const formattedEndDate = toApiDate(endDate);
      
      if (!UTILS.validateSelectedDates(formattedStartDate, formattedEndDate)) return;
      setLocalStateData("loading", true);
      const userDetail = UTILS.getCurrentUser();
      const result = await API.downloadOrderHistoryExcelReport({
        startDate: formattedStartDate,
        endDate: formattedEndDate,
        brandId: userDetail.brandId,
      });
      if (result?.success === 1 && result?.data) {
        UTILS.handleSuccesNotification(
          t("Order History Exported Successfully")
        );
        const url = result?.data;
        downloadReports(url);
      } else {
        UTILS.handleErrorNotification(
          t(result?.error?.message || errorHandler(result?.error))
        );
      }
    } catch (error) {
      errorNotification(errorHandler(error?.error?.message || error?.error));
    } finally {
      setLocalStateData("loading", false);
    }
  };

  /** Ant Design DatePicker passes (moment | null); store ISO string for stable value/display. */
  const handleStartDateChange = (date) => {
    setStartDate(date && date.isValid() ? date.format("YYYY-MM-DD") : null);
  };

  const handleEndDateChange = (date) => {
    setEndDate(date && date.isValid() ? date.format("YYYY-MM-DD") : null);
  };

  useEffect(() => {
    if (!startDate || !endDate) return;
    if (!toApiDate(startDate) || !toApiDate(endDate)) return;
    setPage(1);
    getOrdersHistory(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fetch only when the range changes; handler reads latest dates.
  }, [startDate, endDate]);

  return (
    <AppContainer title={t("Order History")} subTitle={t("Here you can track all your past orders, earnings, and payment details in one place.")} loading={state.loading}>
      <div className="flex w-full flex-col gap-3 py-4 md:flex-row md:flex-wrap md:items-end">
        <div className="w-full min-w-0 md:min-w-[18rem] md:w-80 lg:w-96 md:flex-shrink-0">
          <AppDatePicker
            label={t("Start date")}
            value={startDate || undefined}
            onChange={handleStartDateChange}
          />
        </div>
        <div className="w-full min-w-0 md:min-w-[18rem] md:w-80 lg:w-96 md:flex-shrink-0">
          <AppDatePicker
            label={t("End date")}
            value={endDate || undefined}
            onChange={handleEndDateChange}
          />
        </div>
      </div>

          
        <Stats
          cardEarning={state.codEarning}
          onlineEarning={state.onlineEarning}
        />
        {state.orderHistory?.data?.length > 0 && (
          <div className="flex justify-between items-center mt-3">
            <div className="text-lg font-semibold">
              <span className="text-gray-700 mx-1">{t("Note")}</span>
              <span className="text-gray-400 font-normal text-13px">
                {t(
                  "The online orders income is shown without PAYMENT gateway fees"
                )}
              </span>
            </div>
          </div>
        )}
        <div className="flex flex-col md:flex-row md:justify-between justify-start items-center my-3">
          <AppHeading1>Orders</AppHeading1>
          <div className=" flex flex-col md:flex-row items-center justify-end gap-3 md:max-w-2xl">
            <AppSearchInput
              value={searchTerm}
              placeholder={t("Search by order number, store, or phone")}
              onInput={(e) => setSearchTerm(e.target.value)}
            />
            {state.orderHistory?.data?.length > 0 && (
              <div className="md:mt-0 mt-3">
                <AppButton
                  onClick={downloadexcel}
                  type="primary"
                >
                  {t("Export")}
                </AppButton>
              </div>
              
            )}
          </div>
        </div>

        <OnlineOderTable
          orders={state.orderHistory}
          searchTerm={searchTerm}
        />
    </AppContainer>
  );
};

export default Orders;
